/**
 * MultiSelectOptions (Iter 9.40 - Pass A.4)
 *
 * Canonical multi-select component that mirrors the frontend preference card UX.
 * Used in Master Services / Occasions / Experiences / Features admin forms,
 * AND in dynamic /me/preferences rendering.
 *
 * Per ITER940_FORK_BRIEF § 0 vocabulary lock: this replaces the older
 * `MultiSelectChips` name. The word "chip" is BANNED from UI/component names.
 *
 * Backwards-compat shim: `components/admin/MultiSelectChips.jsx` re-exports
 * this same default export so existing imports keep compiling, but no NEW
 * code should import "MultiSelectChips".
 *
 * Props
 *   value: string[]  - currently-selected option ids/labels.
 *   onChange(next): callback when selection changes.
 *   options: Array<string | {id, label, image_url?}>  - suggestion list.
 *   placeholder, testid, allowCustom: as before.
 *
 * Image-aware: when option objects with `image_url` are provided, each
 * suggestion shows a small thumbnail (12x12) so admin/seeded fields with
 * shade swatches render visually.
 */
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

export default function MultiSelectOptions({
  value = [],
  onChange,
  options = [],
  placeholder = "Type & press Enter",
  testid = "multi",
  allowCustom = true,
}) {
  const [draft, setDraft] = useState("");
  const v = Array.isArray(value) ? value : [];

  // Normalize options to {id,label,image_url} shape.
  const norm = (options || []).map((o) =>
    typeof o === "string"
      ? { id: o, label: o, image_url: null }
      : { id: o.id || o.key || o.label, label: o.label || o.id, image_url: o.image_url || null }
  );

  const add = (raw) => {
    const x = String(raw || "").trim();
    if (!x) return;
    if (!v.includes(x)) onChange([...v, x]);
    setDraft("");
  };
  const remove = (x) => onChange(v.filter((y) => y !== x));

  const suggestions = norm.filter(
    (o) => !v.includes(o.id) && (!draft || o.label.toLowerCase().includes(draft.toLowerCase()))
  );

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {v.map((x) => (
          <Badge
            key={x}
            variant="outline"
            className="gap-1 pr-1"
            data-testid={`${testid}-tag-${x}`}
          >
            {x}
            <button type="button" onClick={() => remove(x)} className="hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <div className="flex items-center gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                allowCustom && add(draft);
              }
              if (e.key === ",") {
                e.preventDefault();
                allowCustom && add(draft);
              }
            }}
            placeholder={placeholder}
            className="h-7 text-xs w-44"
            data-testid={`${testid}-input`}
          />
          {allowCustom && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => add(draft)}
              className="h-7 px-2"
              data-testid={`${testid}-add`}
            >
              <Plus className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      {suggestions.length > 0 && draft && (
        <div className="flex flex-wrap gap-1 mt-2">
          {suggestions.slice(0, 8).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => add(s.id)}
              className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 inline-flex items-center gap-1"
              data-testid={`${testid}-suggest-${s.id}`}
            >
              {s.image_url && (
                <img
                  src={s.image_url}
                  alt=""
                  className="w-3 h-3 rounded-full object-cover"
                  loading="lazy"
                />
              )}
              + {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
